import { useDialog, useRenderNotifier } from '@wui/common-library'
import { SubscriberStatus, useIM } from '@wui/im'
import {
  IncomingAckEvent,
  IncomingDTMFEvent,
  IncomingEvent,
  MediaConstraints,
  OutgoingAckEvent,
  OutgoingDTMFEvent,
  PeerConnectionEvent,
  RTCPeerConnectionDeprecated,
  RTCSession,
  ReferEvent,
  ConnectingEvent,
  OutgoingEvent,
  HoldEvent,
} from '@wui/jssip/lib/RTCSession'
import { IncomingRequest, IncomingResponse, OutgoingRequest } from '@wui/jssip/lib/SIPMessage'
import { validate } from 'uuid'
import { triggerRef } from 'vue'

import router from '@/app/router'
import { useStoreRooms } from '@/app/store/pinia/rooms/rooms'
import { useStoreSettingsNotifications } from '@/app/store/pinia/settings/notifications'
import { useSetStatusHook } from '@/composables/useSetStatusHook'

import {
  startConferenceAwareSessionTimer,
  useCallSessionRoomStore,
  useCallSessionStartedTimestampStore,
  useCreatedFromDialogCallSessionsStore,
} from '@/entities/call-session'
import { useCallSessionMediaStore } from '@/entities/call-session-media'
import { useCallsStore } from '@/entities/calls'
import { useStoreContacts } from '@/entities/contact'

import { SuccessDialog } from '@/shared/components/dialogs'
import { isSubscriber } from '@/shared/composables/local-storage'
import { useLocalization } from '@/shared/composables/localization'
import { usePlayAudio, RINGTONE_CALL_ENDED, AudioPlayer } from '@/shared/composables/play-audio'
import { emitter } from '@/shared/services/emitter'
import { getParticipantShortCallIdFromTrackId } from '@/shared/services/media-helper'
import { useHoldStore } from '@/shared/store/hold'
import { EmitterEvents } from '@/shared/types/emitter-events'
import { GUEST_CALL_SESSION, pathNames } from '@/shared/utils/constants'
import { createPrivateRoom } from '@/shared/utils/im'
import { getAnswerOptions, getCallEndedDialogProps, isRemoteHoldFromSdp, parseMediaConstraintsFromDTMF, parseMediaConstraintsFromSDP, parseMediaConstraintsFromXSatelParams, parseRemotePartyId } from '@/shared/utils/jssip'
import { optionalPush } from '@/shared/utils/router'
import { toPServedUser } from '@/shared/utils/uri'
import { getChatRoute } from '@/shared/utils/url'

import { eventBus } from './event-bus'
import { isAttendedTransferCancelled } from './is-attended-transfer-cancelled'
import { RTCSessionFacade, STATE, STATUS_CODES } from './use-session-facade'
import { useStreamVolume } from './use-stream-volume'
import { useWebRTC } from './use-web-rtc'

interface SessionEventListener {
  addEventListeners: () => void
  removeEventListeners: () => void
}

type EventEndedCause = 'Canceled' | 'Rejected' | 'Busy' | 'SIP Failure Code' | 'Unavailable' | 'Redirected' | 'Not Found' | 'Address Incomplete' | 'Incompatible SDP' | 'Authentication Error'
type EventOriginator = 'local' | 'remote' | 'system'

type FailedEvent = {
  cause: EventEndedCause,
  message: IncomingResponse,
  originator: EventOriginator,
}

type EndedEvent = {
  cause: EventEndedCause,
  message: IncomingRequest | IncomingResponse,
  originator: EventOriginator,
}

let callEndedAudioPlayer: AudioPlayer

export function useSessionEventListener (sessionFacade: RTCSessionFacade): SessionEventListener {
  if (!callEndedAudioPlayer) {
    callEndedAudioPlayer = usePlayAudio(RINGTONE_CALL_ENDED)
  }
  return {
    addEventListeners: () => {
      const { session } = sessionFacade

      if (session.connection) {
        onPeerConnection(session.connection, sessionFacade)
      }

      session.addListener('connecting', (event: ConnectingEvent) => {
        try {
          sessionFacade.callId.value = (event.request as OutgoingRequest).call_id
          if (session && session.connection) {
            const senders = session.connection.getSenders()
            let audioStream: MediaStream | undefined
            let videoStream: MediaStream | undefined
            senders.filter(({ track }) => track && track.enabled).forEach(({ track }) => {
              if (track?.kind === 'audio') {
                audioStream = new MediaStream()
                audioStream.addTrack(track)
                sessionFacade.isMuted.value = false
                sessionFacade.localAudioStream.value = audioStream
                triggerRef(sessionFacade.localAudioStream)
              } else if (track?.kind === 'video') {
                videoStream = new MediaStream()
                videoStream.addTrack(track)
                sessionFacade.isVideoMuted.value = false
                sessionFacade.localVideoStream.value = videoStream
                triggerRef(sessionFacade.localVideoStream)
              }
            })
          }
        } catch (e) {
          console.error(e)
        }
      })

      session.addListener('peerconnection', (e: PeerConnectionEvent) => {
        onPeerConnection(e.peerconnection, sessionFacade)
      })
      session.addListener('progress', (e: OutgoingEvent) => {
        onProgress(e, sessionFacade)
      })
      session.addListener('accepted', (e: IncomingEvent | OutgoingEvent) => {
        const sessionRoom = useCallSessionRoomStore().getRoom(sessionFacade.sessionId)
        if (!('response' in e) || !e.response || sessionRoom?.isGroup) {
          return
        }

        const mediaStore = useCallSessionMediaStore()
        const sessionId = sessionFacade.session.id
        const number = sessionFacade.session.remote_identity.uri.user

        // Для приватных звонков в accepted приоритет у SDP:
        // если в ответе заданы аудио/видео каналы, берем их как source of truth.
        const sdp = e.response.body
        if (sdp) {
          mediaStore.update(sessionId, number, parseMediaConstraintsFromSDP(sdp))
          return
        }

        // Fallback на случай отсутствия SDP.
        const xSatelParams = e.response.getHeader('X-Satel-Params')
        if (xSatelParams) {
          mediaStore.update(sessionId, number, parseMediaConstraintsFromXSatelParams(xSatelParams))
        }
      })
      session.addListener('replaces', async (event: ReferEvent) => {
        const { awaitForRoomAdded, createRoom } = useStoreRooms()
        sessionFacade.replaceOnProgress.value = true
        const roomId = event.request.from.uri.user
        const isGroup = validate(roomId) || roomId.length === 32
        const pServed = toPServedUser(roomId, isGroup)
        if (isGroup) {
          createRoom({
            id: roomId,
            pServedUser: pServed,
            avatar: '',
            isGroup,
            name: event.request.from.display_name,
            number: roomId,
            subscribers: new Map(),
            isExternal: false,
          })
        } else {
          const { fetchContactsByNameOrNumberIfListIsNotLoaded } = useStoreContacts()
          const contacts = await fetchContactsByNameOrNumberIfListIsNotLoaded(roomId)
          const contact = contacts.at(0)
          if (contact) {
            createPrivateRoom(contact.imLogin, contact)
          } else {
            createRoom({
              id: roomId,
              pServedUser: pServed,
              avatar: '',
              isGroup,
              name: event.request.from.display_name,
              number: roomId,
              subscribers: new Map(),
              isExternal: true,
            })
          }
        }
        event.accept(async (session: RTCSession) => {
          awaitForRoomAdded(roomId, 2000)
            .catch(() => {
              useIM().requestBriefStatus({
                contactId: pServed,
              })
            })
            .finally(async () => {
              const options = await getAnswerOptions(true, false, isGroup)
              session.answer(options)
            })
        })
      })
      session.addListener('newDTMF', (e: IncomingDTMFEvent | OutgoingDTMFEvent) => {
        if (e.originator === 'remote') {
          const constraints = parseMediaConstraintsFromDTMF(e.dtmf)
          if (constraints) {
            const sessionId = session.id
            const number = e.request.from.uri.user
            useCallSessionMediaStore().update(sessionId, number, constraints)
          }
        }
      })
      session.addListener('failed', (e: FailedEvent) => {
        onFailed(e, sessionFacade)
      })
      session.addListener('ended', (e: EndedEvent) => {
        onEnded(e, sessionFacade)
      })
      session.addListener('confirmed', (e: IncomingAckEvent | OutgoingAckEvent) => {
        onConfirmed(e, sessionFacade)
      })
      session.addListener('hold', (e: HoldEvent) => {
        onHold(e, sessionFacade)
      })
      session.addListener('unhold', (e: HoldEvent) => {
        onUnhold(e, sessionFacade)
      })
      session.addListener('muted', (e: MediaConstraints) => {
        onMuted(e, sessionFacade)
      })
      session.addListener('unmuted', (e: MediaConstraints) => {
        onUnmuted(e, sessionFacade)
      })
      session.addListener('reinvite', (e) => {
        // WUI-3347: mute/video для приватных звонков — DTMF; hold детектируем из SDP сразу (WUI-4418)
        const sdp = e.request.body
        const sessionId = session.id
        const number = e.request.from.uri.user

        syncRemoteHoldFromReinviteSdp(sdp, sessionFacade)

        const constraints: MediaConstraints = parseMediaConstraintsFromSDP(sdp)
        useCallSessionMediaStore().update(sessionId, number, constraints)
      })
    },

    removeEventListeners: () => {
      const { session } = sessionFacade

      session.removeAllListeners()
    },
  }
}

function onPeerConnection (peerconnection: RTCPeerConnectionDeprecated, sessionFacade: RTCSessionFacade) {
  const sessionRoom = useCallSessionRoomStore().getRoom(sessionFacade.sessionId)
  if (!sessionRoom?.isGroup) {
    const audioStream = sessionFacade.localAudioStream.value
    const videoStream = sessionFacade.localVideoStream.value
    if (audioStream || videoStream) {
      const senders = peerconnection.getSenders()
      if (audioStream) {
        const audioTrack = audioStream.getAudioTracks().find(t => t.enabled) ?? audioStream.getAudioTracks()[0]
        if (audioTrack) {
          const audioSender = senders.find(s => s.track?.kind === 'audio')
          if (audioSender) {
            audioSender.replaceTrack(audioTrack)
          } else {
            peerconnection.addTrack(audioTrack, audioStream)
          }
        }
      }
      if (videoStream) {
        const videoTrack = videoStream.getVideoTracks().find(t => t.enabled) ?? videoStream.getVideoTracks()[0]
        if (videoTrack) {
          const videoSender = senders.find(s => s.track?.kind === 'video')
          if (videoSender) {
            videoSender.replaceTrack(videoTrack)
          } else {
            peerconnection.addTrack(videoTrack, videoStream)
          }
        }
      }
    }

    const { removeStreamsBySessionId } = useCallSessionMediaStore()
    removeStreamsBySessionId(sessionFacade.sessionId)
  }

  peerconnection.addEventListener('track', (e: RTCTrackEvent) => {
    const { addStreams, addStream, getStreams, removeStreamsBySessionIdAndTrackId } = useCallSessionMediaStore()
    const { watch } = useStreamVolume()

    const streams = e.streams as MediaStream[]
    const sessionRoom = useCallSessionRoomStore().getRoom(sessionFacade.sessionId)
    if (!sessionRoom?.isGroup) {
      e.track.addEventListener('ended', () => {
        removeStreamsBySessionIdAndTrackId(sessionFacade.session.id, e.track.id)
      })

      addStreams(sessionFacade.session.id, sessionFacade.number, streams)
      const stream = streams.find(stream => stream.getAudioTracks()?.length > 0)
      if (stream && e.track.kind === 'audio') {
        const stopWatch = watch(stream)
        e.track.addEventListener('ended', () => stopWatch?.())
      }
    } else {
      // Only the new track — re-wrapping every track in e.streams recreates MediaStream
      // ids for unrelated participants and remounts all tiles with loaders.
      const participantShortCallId = getParticipantShortCallIdFromTrackId(e.track.id)
      if (!participantShortCallId) {
        return
      }

      const existingStreams = getStreams(sessionFacade.session.id, participantShortCallId)
      const alreadyStored = existingStreams?.some((stored) =>
        stored.getTracks().some((track) => track.id === e.track.id),
      )
      if (alreadyStored) {
        return
      }

      e.track.addEventListener('ended', () => {
        removeStreamsBySessionIdAndTrackId(sessionFacade.session.id, e.track.id)
      })

      const streamFromTrack = new MediaStream([e.track])
      addStream(sessionFacade.session.id, participantShortCallId, streamFromTrack)

      if (e.track.kind === 'audio') {
        const stopWatch = watch(streamFromTrack)
        e.track.addEventListener('ended', () => stopWatch?.())
      }
    }
  })
}

function onProgress (event: OutgoingEvent, sessionFacade: RTCSessionFacade) {
  const { session, sessionState } = sessionFacade
  const { direction } = session
  sessionState.value = direction === 'outgoing' ? STATE.PROGRESS : STATE.RINGING

  handleRemotePartyId(event, sessionFacade)
}

function onFailed (e: FailedEvent, sessionFacade: RTCSessionFacade) {
  const { sessionState, sessionId, callId } = sessionFacade
  const room = useCallSessionRoomStore().getRoom(sessionId)
  const { isOutsideRoom: isOutside, removeRoom } = useStoreRooms()

  if (e.originator === 'local' && e.cause === 'Rejected') {
    sessionState.value = STATE.CANCELED
  } else if (e.originator === 'local' && e.cause === 'Canceled') {
    sessionState.value = STATE.CANCELED
  } else if (e.originator === 'remote' && e.cause === 'Canceled') {
    sessionState.value = STATE.CANCELED
  } else if (e.originator === 'remote' && e.cause === 'Unavailable') {
    sessionState.value = STATE.UNAVAILABLE
  } else if (e.originator === 'remote' && e.cause === 'Busy') {
    const reasonMessage = e.message.getHeaders('Reason')?.[0]

    const status = reasonMessage?.split(';').reduce((acc, el) => {
      const [key, value] = el.split('=')

      if (key === 'cause') {
        return value ?? ''
      }

      return acc
    }, '')

    if (Number(status) === STATUS_CODES.BUSY_HERE) {
      sessionState.value = STATE.REJECTED
    } else {
      sessionState.value = STATE.BUSY
    }

    emitter.emit(EmitterEvents.REMOTE_BUSY)
  } else if (e.originator === 'remote' && e.cause === 'Rejected') {
    sessionState.value = STATE.OFFLINE
  } else if (e.originator === 'remote' && e.cause === 'SIP Failure Code') {
    sessionState.value = STATE.OFFLINE
  } else {
    sessionState.value = STATE.ERROR
  }
  sessionFacade.timer.stop()
  if (sessionFacade.replaceOnProgress.value) {
    setTimeout(() => {
      eventBus.emit('removeSession', sessionId) // Нужен, чтобы sessionFacade не удалялся раньше, чем мы перейдем на новый route для create conference from tet-a-tet.
    }, 3000)
  } else {
    eventBus.emit('removeSession', sessionId)
  }
  userStatusEndedHandler(sessionId)

  const isExternalRoom = room.isExternal
  const isOutsideRoom = isOutside(room)

  if (isExternalRoom || isOutsideRoom) {
    removeRoom(room.id)
  }

  if (sessionFacade.callId.value) {
    const { removeCallMediaRequest } = useCallsStore()
    removeCallMediaRequest(sessionFacade.callId.value)
  }

  eventBus.emit('callEnded', callId.value)

  if (room.isGroup) {
    eventBus.emit('callEndedInRoom', room.pServedUser)
  }
}

function onEnded (e: EndedEvent, sessionFacade: RTCSessionFacade) {
  const { sessions, getRTCSessionFacadeById, setActiveSession } = useWebRTC()
  const { session, sessionId, callId } = sessionFacade
  const room = useCallSessionRoomStore().getRoom(sessionId)
  const { name, params } = router.currentRoute.value

  const { showDialog } = useDialog<void>()
  const { notifyInfo } = useRenderNotifier()
  const { t } = useLocalization()
  const isSessionPath = name === pathNames.CALL_SESSION || name === pathNames.GUEST_CALL_SESSION
  const isExternalRoom = room.isExternal
  const isNotReplace = !sessionFacade.replaceOnProgress.value

  const originalSessionId = sessionFacade.referToSessionId.value
  const originalSession = originalSessionId ? getRTCSessionFacadeById(originalSessionId) : undefined
  const isAttendedTransferCancel = isAttendedTransferCancelled(
    sessionId,
    originalSessionId,
    originalSession?.sessionState.value,
  )

  useHoldStore().removeState(sessionId)

  if ((!callId.value || !useCreatedFromDialogCallSessionsStore().hasCallId(callId.value)) && isNotReplace && !isAttendedTransferCancel) {
    callEndedAudioPlayer.play()
  }

  if (isAttendedTransferCancel && originalSessionId) {
    setActiveSession(originalSessionId)
    router.push({
      name: isSubscriber() ? 'CallSession' : GUEST_CALL_SESSION,
      params: { sessionId: originalSessionId },
    })
  } else if (isSubscriber()) {
    if (isSessionPath) {
      const { roomsList, isOutsideRoom, removeRoom } = useStoreRooms()
      const foundRoom = roomsList.find(room => !room.isExternal && !isOutsideRoom(room))
      if (isExternalRoom || isOutsideRoom(room)) { // Если комната с внешним абонентом, которого нет в списке contacts или который есть в списке контактов
        const chatRoute = foundRoom ? getChatRoute(foundRoom) : null
        if (chatRoute) {
          router.push(chatRoute)
        } else {
          router.push({ name: 'Main' })
        }
        removeRoom(room.id)
      } else if (sessions.value.size === 1) { // Если сессия одна
        optionalPush(getChatRoute(room))
      } else if (sessions.value.size > 1) {
        const onCurrentCallPage = params.sessionId === sessionId
        const sessionsList = [...sessions.value.values()]
        const connectedOther = sessionsList.find(
          s => s.sessionId !== sessionId && s.sessionState.value === STATE.CONNECTED,
        )
        if (onCurrentCallPage) {
          if (connectedOther && connectedOther.referToSessionId.value !== router.currentRoute.value.params.sessionId) {
            router.push({ name: 'CallSession', params: { sessionId: connectedOther.sessionId }})
          } else if (connectedOther?.referToSessionId?.value) {
            optionalPush(getChatRoute(room))
          } else if (connectedOther) {
            router.push({ name: 'CallSession', params: { sessionId: connectedOther.sessionId }})
          } else {
            optionalPush(getChatRoute(room))
          }
        } else {
          // Завершился вызов, отличный от открытого в URL (частый случай attended transfer: вторая BYE)
          optionalPush(getChatRoute(room))
        }
      } else if (sessions.value.size === 0) {
        router.push({ name: 'Main' })
      } else {
        console.warn('It will never happen', sessions.value.size)
      }
    }
  } else {
    router.back()
  }

  if (sessionFacade.replaceOnProgress.value) {
    setTimeout(() => {
      eventBus.emit('removeSession', session.id) // Нужен, чтобы sessionFacade не удалялся раньше, чем мы перейдем на новый route для create conference from tet-a-tet.
    }, 3000)
  } else if (isAttendedTransferCancel) {
    notifyInfo(t('common.attendedTransferCancelled'))
    eventBus.emit('removeSession', session.id)
  } else {
    showDialog(SuccessDialog, { ...getCallEndedDialogProps(e, t), hasOverlay: true }).then(() => {
      eventBus.emit('removeSession', session.id)
    })
  }
  userStatusEndedHandler(sessionId)

  eventBus.emit('callEnded', callId.value)

  if (room.isGroup) {
    eventBus.emit('callEndedInRoom', room.pServedUser)
  }
}

function onConfirmed (e: IncomingAckEvent | OutgoingAckEvent, sessionFacade: RTCSessionFacade) {
  const { sessionId, dialog, sessionState, referToSessionId } = sessionFacade

  eventBus.emit('confirmed', sessionFacade?.sessionId)
  dialog.closeDialog.value?.(true)

  sessionState.value = STATE.CONNECTED

  if (isSubscriber()) {
    if (referToSessionId.value !== router.currentRoute.value.params.sessionId) {
      router.push({ name: 'CallSession', params: { sessionId }})
    }
  } else {
    router.push({ name: GUEST_CALL_SESSION, params: { sessionId }})
  }

  const { startedCallsTimestamps } = useCallSessionStartedTimestampStore()
  const room = useCallSessionRoomStore().getRoom(sessionFacade.sessionId)
  const timer = sessionFacade.timer
  startConferenceAwareSessionTimer(timer, room.isGroup, room.pServedUser, startedCallsTimestamps)

  userStatusConfirmedHandler()

  const { removeCallMediaRequest } = useCallsStore()
  const ack = (e as IncomingAckEvent)?.ack as IncomingRequest & { call_id: string }

  if (ack?.call_id) {
    removeCallMediaRequest(ack.call_id)
  }
}

function syncRemoteHoldFromReinviteSdp (sdp: string, sessionFacade: RTCSessionFacade) {
  if (useCallSessionRoomStore().getRoom(sessionFacade.sessionId).isGroup) {
    return
  }

  const holdStore = useHoldStore()
  const sessionId = sessionFacade.sessionId
  const isRemoteHold = isRemoteHoldFromSdp(sdp)
  const wasRemoteHold = holdStore.getIsRemoteHold(sessionId)
  if (isRemoteHold && !wasRemoteHold) {
    onHold({ originator: 'remote' } as HoldEvent, sessionFacade)
  } else if (!isRemoteHold && wasRemoteHold) {
    onUnhold({ originator: 'remote' } as HoldEvent, sessionFacade)
  }
}

function onHold (e: HoldEvent, sessionFacade: RTCSessionFacade) {
  useHoldStore().setState(sessionFacade.sessionId, e.originator, true)

  eventBus.emit('hold', sessionFacade?.sessionId)
  sessionFacade.setState(STATE.ONHOLD)
}

function onUnhold (e: HoldEvent, sessionFacade: RTCSessionFacade) {
  const holdStore = useHoldStore()
  holdStore.setState(sessionFacade.sessionId, e.originator, false)

  eventBus.emit('unhold', sessionFacade?.sessionId)
  // WUI-3349: в тет-а-тет переходим в CONNECTED только когда оба абонента сняли удержание
  const isLocalHold = holdStore.getIsLocalHold(sessionFacade.sessionId)
  const isRemoteHold = holdStore.getIsRemoteHold(sessionFacade.sessionId)
  const sessionRoom = useCallSessionRoomStore().getRoom(sessionFacade.sessionId)
  if (sessionRoom?.isGroup || (!isLocalHold && !isRemoteHold)) {
    sessionFacade.setState(STATE.CONNECTED)
  }
}

function onMuted (e: MediaConstraints, sessionFacade: RTCSessionFacade) {
  if (e.audio) {
    sessionFacade.isMuted.value = true
  }
  if (e.video) {
    sessionFacade.isVideoMuted.value = true
  }
}

function onUnmuted (e: MediaConstraints, sessionFacade: RTCSessionFacade) {
  if (e.audio) {
    sessionFacade.isMuted.value = false
  }
  if (e.video) {
    sessionFacade.isVideoMuted.value = false
  }
}

const userStatusConfirmedHandler = () => {
  const { callStatusDoNotDisturb, applyCallStatusDoNotDisturb } = useStoreSettingsNotifications()
  const { setUpdateDNDonSetStatus } = useSetStatusHook()
  if (!callStatusDoNotDisturb.enabled) return

  applyCallStatusDoNotDisturb().then((isSuccess) => {
    if (isSuccess) {
      setUpdateDNDonSetStatus(SubscriberStatus.DO_NOT_DISTURB)
    }
  })
}

export const userStatusEndedHandler = (sessionId: string) => {
  const { callStatusDoNotDisturb, resetCallStatusIfChangedByCall } = useStoreSettingsNotifications()
  const { sessions } = useWebRTC()
  const { setUpdateDNDonSetStatus } = useSetStatusHook()
  if (!callStatusDoNotDisturb.enabled) return
  if (sessions.value.size === 1) {
    const id = sessions.value.entries().next().value?.[0]
    if (sessionId === id) {
      resetCallStatusIfChangedByCall()
      setUpdateDNDonSetStatus(callStatusDoNotDisturb.oldStatusNumber)
    }
  }
}

const handleRemotePartyId = async (event: OutgoingEvent, sessionFacade: RTCSessionFacade) => {
  if (event.response?.hasHeader('Remote-Party-Id')) {
    const { findByInternalNumber, fetchContactsByNameOrNumberIfListIsNotLoaded } = useStoreContacts()
    const number = event.response.to.uri.user
    let contact = findByInternalNumber(number)
    if (contact) {
      return
    }
    await fetchContactsByNameOrNumberIfListIsNotLoaded(number)
    contact = findByInternalNumber(number)
    if (contact) {
      return
    }

    const parsed = parseRemotePartyId(event.response.getHeader('Remote-Party-Id'))

    if (parsed) {
      sessionFacade.displayName.value = parsed
      const room = useCallSessionRoomStore().getRoom(sessionFacade.sessionId)
      if (room) {
        room.name = parsed
      }
    }
  }
}
